/*
 * Copyright (c) 2024-2026, s0up and the autobrr contributors.
 * SPDX-License-Identifier: GPL-2.0-or-later
 */

import React, { useEffect, useRef, useState } from "react";

const reduceMotion = () =>
  typeof window !== "undefined" && window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;

/**
 * A number that glides to its new value instead of jumping. It counts up from
 * `mountFrom` (default 0) when it first appears; pass `mountFrom={null}` to
 * show the first value at once. The text is a single text node, so the page
 * translation keeps working. Reduced motion jumps straight to the value.
 */
export const AnimatedNumber: React.FC<{
  value: number;
  /** how the number is written; default: rounded integer */
  format?: (n: number) => string;
  duration?: number;
  mountFrom?: number | null;
  className?: string;
}> = ({ value, format = (n) => String(Math.round(n)), duration = 650, mountFrom = 0, className }) => {
  const [display, setDisplay] = useState(mountFrom === null ? value : mountFrom);
  const current = useRef<number | null>(null); // where the glide currently is

  useEffect(() => {
    const from = current.current ?? display;
    if (from === value) {
      current.current = value;
      return;
    }
    const instant = reduceMotion() || !isFinite(from) || !isFinite(value);
    const start = performance.now();
    let raf = 0;
    const step = (now: number) => {
      const t = instant ? 1 : Math.min(1, (now - start) / duration);
      const eased = 1 - Math.pow(1 - t, 3); // strong ease-out: quick start, soft landing
      current.current = from + (value - from) * eased;
      setDisplay(current.current);
      if (t < 1) raf = requestAnimationFrame(step);
    };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
    // `display` is only the starting point on the first run
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, duration]);

  return <span className={className}>{format(display)}</span>;
};
